import { allParents, findParent, hasParent } from "UI/UI.ElementarControls/Utils/CustomElementUtils";
import { IRegisteredStaticEventListener } from "./Control";

export class GenericControl {
    private static _listeners: Map<Element | Document, Array<IRegisteredStaticEventListener>> = new Map<Element | Document, Array<IRegisteredStaticEventListener>>();
    private static _counterElement: number = 0;

    public static getID(): string {
        if (!GenericControl._counterElement)
            GenericControl._counterElement = 0;
        return `_id_control_${GenericControl._counterElement++}`; // `
    }

    public static _constructor(element: HTMLElement): void {
        element.classList.add("CustomControl");
    }

    /**
     * Metoda, která vrací true nebo false, na základě fyzické viditelnosti prvku
     */
    public static isElementVisible(element: HTMLElement): boolean {
        return GenericControl.allParents(element, (el: HTMLElement) => el.style.display !== "none");
    }

    /**
     * Hledá první rodičovský element předaného elementu, který odpovídá zadanému predikátu
     * @param startElement Element, kterého rodiče se mají prohledávat
     * @param comparePredicate Porovnávací predikát nebo selector, první vyhovující rodič je vrácen nebo null
     */
    public static findParent(startElement: HTMLElement, comparePredicate: string | ((el: HTMLElement) => boolean)): HTMLElement {
        return findParent(startElement, comparePredicate);
    }

    /**
     * Hledá, zda existuje nějaký rodičovský element předaného elementu, který odpovídá zadanému predikátu
     * @param startElement Element, kterého rodiče se mají prohledávat
     * @param comparePredicate Porovnávací predikát nebo selector
     */
    public static hasParent(startElement: HTMLElement, comparePredicate: string | ((el: HTMLElement) => boolean)): boolean {
        return hasParent(startElement, comparePredicate);
    }

    /**
     * Kontroluje, zda všechny rodičovské elementy předaného elementu odpovídají zadanému predikátu
     * @param startElement Element, kterého rodiče se mají prohledávat
     * @param comparePredicate Porovnávací predikát nebo selector
     * @param checkStartElement Kontrola začíná už na předaném startElement-u
     */
    public static allParents(startElement: HTMLElement, comparePredicate: string | ((el: HTMLElement) => boolean), checkStartElement: boolean = true): boolean {
        return allParents(startElement, comparePredicate, checkStartElement);
    }

    /**
     * Vrátí pozici elementu v rámci dokumentu
     * @param elem Element, kterého se to týka
     */
    public static offset(elem: HTMLElement): { top: number; left: number; width: number; height: number } {
        const doc = elem && elem.ownerDocument;
        if (!doc) {
            return null;
        }

        const docElem = doc.documentElement;
        const box = elem.getBoundingClientRect();
        const win = doc.defaultView;
        return {
            top: box.top + win.pageYOffset - docElem.clientTop,
            left: box.left + win.pageXOffset - docElem.clientLeft,
            width: box.width,
            height: box.height,
        };
    }

    public static attributeChangedCallback(element: HTMLElement, attrName: string, oldVal: string, newVal: string): void {
        GenericControl.callEvent(element, "attributechange", {
            attrName: attrName,
            oldVal: oldVal,
            newVal: newVal,
        });
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    public static callEvent(element: HTMLElement, eventName: string, detail?: any, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        const param = {
            detail: detail,
            bubbles: bubbles,
            cancelable: cancelable,
        };
        const evt = new CustomEvent(eventName, param);
        element.dispatchEvent(evt);

        return evt;
    }

    /**
     * Zaregistruje událost do skupiny udalosti
     * @param groupId ID skupiny udalosti
     * @param element Element pro ktery se bude udalost registrovat
     * @param event Nazev udalosti
     * @param action Akce vyvolana pri udalosti
     */
    public static registerEventListener(element: Element | Document, event: string, action: (e: Event) => void): void {
        let data: Array<IRegisteredStaticEventListener>;
        if (GenericControl._listeners.has(element))
            data = GenericControl._listeners.get(element);
        else
            data = [];

        data.push({
            action: action,
            event: event,
            element: element,
            options: {
                reregister: false,
                once: false,
            },
        });

        GenericControl._listeners.set(element, data);
        element.addEventListener(event, action);
    }

    /**
     * Odregistruje akce pro danou skupinu
     * @param groupId Identifikator skupiny
     * @param element Pokud chceme odebrat pro konkretni element
     * @param event Pokud chceme v elementu odebrat pro konkretni udalost
     * @param action Pokud chceme v udalosti odebrat pro konkretní akci
     */
    public static unregisterEventListener(element: Element | Document, event?: string, action?: (e: Event) => void): void {
        if (!GenericControl._listeners.has(element))
            return;

        const data = GenericControl._listeners.get(element);
        const newData: Array<IRegisteredStaticEventListener> = [];
        const allDelete = !event && !action;

        for (const d of data) {
            if (allDelete) {
                d.element.removeEventListener(d.event, d.action);
            } else {
                if (element) {
                    if (element == d.element) {
                        if (event) {
                            if (event === d.event) {
                                if (action) {
                                    if (action == d.action) {
                                        d.element.removeEventListener(d.event, d.action);
                                    } else
                                        newData.push(d);
                                } else
                                    d.element.removeEventListener(d.event, d.action);
                            } else
                                newData.push(d);
                        } else
                            d.element.removeEventListener(d.event, d.action);
                    } else {
                        newData.push(d);
                    }
                } else {
                    d.element.removeEventListener(d.event, d.action);
                }
            }
        }

        if (newData.length === 0)
            GenericControl._listeners.delete(element);
        else if (newData.length !== data.length)
            GenericControl._listeners.set(element, newData);
    }
}
